import React, { useEffect } from 'react';
import { bool, oneOfType, shape, string, arrayOf } from 'prop-types';
import Spinner from '@shared_modules/rc-spinner';
import { Button } from '@shared_modules/local-propulsion/components';
import errorsMapping from './errorsMapping';
import { prescreenType } from '@shared_modules/pss-entry';
import { heapTrackEvent, heapTrackErrors } from '@shared_modules/baui-heap-tracking';
import classNames from 'classnames';

import './PlatformError.scss';
import { trackCleanSweepView } from '../../pages/iloc/utils/heap-tracking';

export default function PlatformError({
  heapTrackEventText,
  flowName,
  errorMessage = '',
  status,
  heapTrackCustomEventName,
  heapExtraProperties,
  redirect = undefined,
  replacements,
  moreDetails,
}) {
  const errorMapping = errorsMapping[flowName.toUpperCase()];

  // If no error found on error mapping will show default error
  if (!Object.keys(errorMapping).includes(errorMessage)) {
    errorMessage = 'DEFAULT';
  }

  let replacedData = { ...errorMapping[errorMessage] };

  if (replacements) {
    replacements.forEach(({ target, replacement }) => {
      Object.keys(replacedData).forEach(key => {
        if (typeof replacedData[key] === 'string') {
          replacedData[key] = replacedData[key].replace(target, replacement);
        }
      });
    });
  }

  const {
    header = '',
    title,
    description,
    subText,
    iconImg,
    buttonText,
    buttonAction,
    buttonHref,
    buttonClasses = '',
    buttonVariant = 'tertiary',
    buttonHrefEventName,
    heapEventView,
    productType,
    containerClasses = '',
  } = replacedData;

  const buttonClassesJoin = classNames('PlatformError-button', buttonClasses);

  const triggerHeapEvent = mode => {
    heapEventView && trackCleanSweepView(heapEventView);
    const defaultName = heapTrackCustomEventName || `${heapTrackEventText} Error - ${mode}`;
    const eventName = mode === 'Submit' ? buttonHrefEventName || defaultName : defaultName;
    const trackingPayload = {
      ERROR_KEYS: errorMessage,
      ERROR_STATUS: status,
      PRODUCT_TYPE: prescreenType[flowName] || productType,
      ERROR_TYPE: errorMessage,
      ERROR_MESSAGE_SHOWN: description,
      ...heapExtraProperties,
    };
    heapTrackEvent(eventName, trackingPayload);
    heapTrackErrors([{ error: errorMessage, message: `${description} - ${buttonText}` }]);
  };

  useEffect(() => {
    let timer;
    triggerHeapEvent('View');
    if (redirect?.type === prescreenType[flowName] && redirect?.status) {
      timer = setTimeout(() => {
        buttonClick(redirect.url);
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, []);

  const buttonClick = redirectHref => {
    triggerHeapEvent('Submit');
    window.location.assign(redirectHref || buttonHref);
  };

  const buttonProps = buttonAction ? { onClick: buttonAction } : { onClick: () => buttonClick() };

  return (
    <div className={classNames('PlatformError-page', containerClasses)}>
      {iconImg && <img className="PlatformError-icon" src={iconImg} alt="" aria-hidden="true" />}
      {header && <div className="PlatformError-header">{header}</div>}
      <h1 className="PlatformError-title">{title}</h1>
      <h2 className="PlatformError-description">{description}</h2>
      {subText && <h3 className="PlatformError-subtext">{subText}</h3>}
      <Spinner header="" show={redirect?.status} />
      {moreDetails}
      {buttonText && (
        <Button name="PlatformErrorButton" className={buttonClassesJoin} variant={buttonVariant} {...buttonProps}>
          {buttonText}
        </Button>
      )}
    </div>
  );
}

PlatformError.propTypes = {
  heapTrackEventText: string.isRequired,
  heapExtraProperties: shape({}),
  flowName: string.isRequired,
  errorMessage: string,
  status: string.isRequired,
  redirect: oneOfType([shape({}), bool]),
  replacements: arrayOf(
    shape({
      target: string.isRequired,
      replacement: string.isRequired,
    })
  ),
  moreDetails: shape({}),
};

PlatformError.defaultProps = {
  heapExtraProperties: {},
};

